import { TILE } from '@/gfx/tiles';
import { angleDiff, dist, facingAngle, facingFromVec } from '@/engine/math';
import type { Game } from './game';
import type { Character } from './entities';
import { findPath } from './nav';
import { moveBox, tileOf, tileCenter, unstick, boxFree } from './physics';
import { idx, roomAtPx } from '@/world/builder';
import { walkableStairsToward } from './routing';
import { reachableHidingPlace, type HidingPlace } from './hiding';
import { SPEED, NOISE_R } from './game';
import { footstepSurface } from './sound';
import { hasLos } from '@/gfx/lighting';
import { drainFlashlight } from './flashlight';

const floorRouteGoals = new WeakMap<Character, string>();
const walkGoals = new WeakMap<Character, { floor: string; x: number; y: number }>();
const hidingGoals = new WeakMap<Character, { floor: string; place: HidingPlace | null; until: number; maxD: number }>();

function hidingPlace(g: Game, c: Character, maxD: number): HidingPlace | null {
  const old = hidingGoals.get(c);
  if (old && old.floor === c.floor && old.maxD === maxD && old.until > g.time &&
      (!old.place || old.place.prop.occupants.length < old.place.prop.kind.hide!.cap)) return old.place;
  const place = reachableHidingPlace(g.floorOf(c), c.x, c.y, maxD);
  hidingGoals.set(c, { floor: c.floor, place, until: g.time + 0.75, maxD });
  return place;
}

function atHidingPlace(g: Game, c: Character, spot: HidingPlace): boolean {
  const f = g.floorOf(c), p = spot.prop;
  return dist(c.x, c.y, spot.x, spot.y) < 10 && roomAtPx(f, c.x, c.y)?.id === p.room &&
    hasLos(c.x, g.eyeY(c), (p.x + p.kind.fw / 2) * TILE, (p.y + p.kind.fh / 2) * TILE, g.reachSegs(f));
}

/** Followers: every free friend that isn't the leader. */
export function updateFollowers(g: Game, dt: number): void {
  const leader = g.leader;
  let cannotHide = 0;
  for (const c of g.chars.values()) {
    if (c.id === leader.id) continue;
    c.stuck = Math.max(0, c.stuck - dt);
    if (c.bubble && (c.bubble.t -= dt) <= 0) c.bubble = null;
    if (c.state === 'groggy') {
      c.groggy -= dt;
      c.moving = false;
      if (c.groggy <= 0) c.state = 'free';
      continue;
    }
    if (c.state === 'hidden') {
      // come out when told to follow and the leader is not hiding
      if (c.mode === 'follow' && leader.state !== 'hidden' && leader.floor === c.floor && dist(c.x, c.y, leader.x, leader.y) < 200) {
        g.unhide(c);
      }
      continue;
    }
    if (c.state !== 'free') continue;
    if (drainFlashlight(c, dt)) c.bubble = { text: '불이 꺼졌어', t: 3 };
    c.flashAngle += angleDiff(c.flashAngle, facingAngle(c.facing)) * (1 - Math.exp(-dt * 22));
    c.moving = false;
    unstick(g.floorOf(c), c);
    if (c.mode === 'wait') { hidingGoals.delete(c); c.gait = 'sneak'; continue; }
    if (c.mode === 'hide') {
      c.flashOn = false;
      const spot = hidingPlace(g, c, 140);
      if (!spot) {
        c.mode = 'wait'; c.gait = 'sneak'; c.path = null;
        c.bubble = { text: '숨을 곳이 없어', t: 3 }; cannotHide++;
        continue;
      }
      if (atHidingPlace(g, c, spot)) { g.hide(c, spot.prop, false); continue; }
      walkTo(g, c, spot.x, spot.y, 'sneak', dt);
      continue;
    }
    // follow
    if (leader.state === 'hidden' || leader.state === 'captured' || leader.state === 'carried') {
      // leader hid: find our own spot nearby, else crouch
      if (leader.floor === c.floor && dist(c.x, c.y, leader.x, leader.y) < 160) {
        c.flashOn = false;
        const spot = hidingPlace(g, c, 90);
        if (spot) {
          if (atHidingPlace(g, c, spot)) { g.hide(c, spot.prop, false); continue; }
          walkTo(g, c, spot.x, spot.y, 'sneak', dt);
          continue;
        }
      }
      c.gait = 'sneak';
      continue;
    }
    hidingGoals.delete(c);
    if (c.floor !== leader.floor) {
      const f = g.floorOf(c);
      let s = c.travelTo?.floor === leader.floor ? f.stairs.find(s => s.def.id === c.travelTo!.stairsId) : null;
      const routeGoal = `${c.floor}:${leader.floor}`;
      if (floorRouteGoals.get(c) !== routeGoal || c.stuck <= 0 || c.travelTo && !s) {
        s = walkableStairsToward(g.mansion, c, leader.floor, d => d.open || !d.locked && d.revealed);
        c.travelTo = s ? { floor: leader.floor, stairsId: s.def.id } : null;
        floorRouteGoals.set(c, routeGoal);
        c.stuck = 1.5;
      }
      if (!s) { c.gait = 'sneak'; continue; }
      const tx = tileCenter(s.far.x), ty = tileCenter(s.far.y);
      if (dist(c.x, c.y, tx, ty) < 6) { g.travel(c, s.def.toFloor, s.def.to); continue; }
      walkTo(g, c, tx, ty, leader.gait === 'sneak' ? 'sneak' : 'walk', dt, 4);
      continue;
    }
    c.travelTo = null;
    const d = dist(c.x, c.y, leader.x, leader.y);
    const slot = followSlot(g, c);
    // Catch up without breaking the player's quiet approach. A distant follower
    // should not start running merely because the leader is creeping away.
    const gait = leader.gait === 'sneak' ? 'sneak' : d > 120 ? 'run' : leader.gait;
    if (d > 30) {
      const f = g.floorOf(leader);
      let tx = leader.x + slot[0], ty = leader.y + slot[1];
      if (!boxFree(f, tx, ty, door => door.open || !door.locked && door.revealed) ||
          !hasLos(leader.x, g.eyeY(leader), tx, g.eyeY({ y: ty }), g.reachSegs(f))) {
        tx = leader.x; ty = leader.y;
      }
      walkTo(g, c, tx, ty, gait, dt, 8, d > 48 ? 1.25 : 1);
    }
    else { c.gait = leader.gait; c.facing = leader.facing; }
  }
  if (cannotHide) g.say(`숨을 곳을 찾지 못한 친구 ${cannotHide}명은 조용히 기다린다.`, 3);
  separate(g);
}

function followSlot(g: Game, c: Character): [number, number] {
  const ids = [...g.chars.keys()].filter((id) => id !== g.leaderId);
  const k = ids.indexOf(c.id);
  const offs: Array<[number, number]> = [[-14, 10], [14, 10], [0, 20], [-24, 22], [24, 22], [0, 32], [-12, 34]];
  return offs[k % offs.length];
}

function walkTo(g: Game, c: Character, tx: number, ty: number, gait: 'sneak' | 'walk' | 'run', dt: number, arrive = 8, speedMul = 1): void {
  const f = g.floorOf(c);
  c.gait = gait;
  let speed = SPEED[gait] * speedMul;
  if (gait === 'run' && c.def.trait.runMul) speed *= c.def.trait.runMul;
  c.pathTimer -= dt;
  const previousGoal = walkGoals.get(c), txTile = tileOf(tx), tyTile = tileOf(ty);
  const goalChanged = !previousGoal || previousGoal.floor !== f.id || previousGoal.x !== txTile || previousGoal.y !== tyTile || c.pathTimer <= 0;
  if (goalChanged) {
    c.path = findPath(f, tileOf(c.x), tileOf(c.y), txTile, tyTile, (x, y) => {
      const d = f.doorAt.get(idx(f, x, y));
      return !!d && (d.open || (!d.locked && d.revealed));
    }, 4000);
    c.pathTimer = 0.35;
    walkGoals.set(c, { floor: f.id, x: txTile, y: tyTile });
  }
  let gx = tx, gy = ty;
  let waypoint = false;
  if (c.path) {
    while (c.path.length && dist(c.x, c.y, tileCenter(c.path[0][0]), tileCenter(c.path[0][1])) < 2.5) c.path.shift();
    if (c.path.length > 1 || (c.path.length === 1 && dist(c.x, c.y, tx, ty) > 24)) {
      gx = tileCenter(c.path[0][0]);
      gy = tileCenter(c.path[0][1]);
      waypoint = true;
    }
  }
  // An unavailable route is not permission to walk straight into the wall.
  if (!c.path) return;
  const dx = gx - c.x, dy = gy - c.y;
  const d = Math.hypot(dx, dy);
  if (d < (waypoint ? 0.5 : arrive)) return;
  const step = Math.min(d, speed * dt);
  const oldX = c.x, oldY = c.y;
  const blocked = moveBox(f, c, (dx / d) * step, (dy / d) * step, g.doorRuleFor(c));
  if (blocked) c.pathTimer = 0;
  if (dist(oldX, oldY, c.x, c.y) < 0.01) return;
  c.moving = true;
  c.facing = facingFromVec(dx, dy, c.facing);
  c.anim += dt * (gait === 'run' ? 12 : gait === 'sneak' ? 5 : 8);
  c.stepTimer -= dt;
  if (c.stepTimer <= 0) {
    c.stepTimer = gait === 'run' ? 0.25 : gait === 'sneak' ? 0.6 : 0.4;
    let r = NOISE_R[gait];
    r *= footstepSurface(roomAtPx(f, c.x, c.y)?.floor ?? 'wood');
    if (c.def.trait.noiseMul) r *= c.def.trait.noiseMul;
    g.emitNoise({ floor: c.floor, x: c.x, y: c.y, r, kind: 'step', from: c.id });
    g.audio.step(gait, c.id === g.leaderId ? 1 : 0.35);
  }
}

/** keep followers from stacking on the same pixel */
function separate(g: Game): void {
  const list = [...g.chars.values()].filter((c) => c.state === 'free');
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i], b = list[j];
      if (a.floor !== b.floor) continue;
      let d = dist(a.x, a.y, b.x, b.y);
      if (d > 9) continue;
      let nx = 1, ny = 0;
      if (d > 0.01) { nx = (b.x - a.x) / d; ny = (b.y - a.y) / d; } else d = 0;
      const push = (9 - d) / 2;
      const f = g.floorOf(a);
      if (a.id !== g.leaderId) moveBox(f, a, -nx * push, -ny * push, () => false);
      if (b.id !== g.leaderId) moveBox(f, b, nx * push, ny * push, () => false);
    }
}

/** Tab: cycle control to the next controllable friend. */
export function controllable(g: Game): string[] {
  return [...g.chars.keys()].filter((id) => {
    const c = g.chars.get(id)!;
    return c.state === 'free' || c.state === 'hidden' || c.state === 'groggy';
  });
}

export function switchLeader(g: Game, dir = 1): boolean {
  const ids = controllable(g);
  if (!ids.length || ids.length === 1 && ids[0] === g.leaderId) return false;
  const i = ids.indexOf(g.leaderId);
  return switchLeaderTo(g, ids[(i + dir + ids.length) % ids.length]);
}

/** Hand control to one specific friend, as when their portrait is tapped. */
export function switchLeaderTo(g: Game, next: string): boolean {
  if (next === g.leaderId || !controllable(g).includes(next)) return false;
  const old = g.leader;
  old.mode = 'follow';
  old.path = null;
  g.leaderId = next;
  const c = g.leader;
  c.mode = 'follow';
  c.path = null;
  return true;
}
